const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details, summary';

/**
 * Native radio groups only put one option in the tab order: the checked radio, or the first
 * if none are checked. Mirror that when building a focusable list (e.g. for focus traps).
 */
export function filterRadioGroups(elements: HTMLElement[]): HTMLElement[] {
  const radiosByName = new Map<string, HTMLInputElement[]>();

  for (const el of elements) {
    if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || !el.name) continue;
    const group = radiosByName.get(el.name) ?? [];
    group.push(el);
    radiosByName.set(el.name, group);
  }

  const tabbableRadio = new Set<HTMLInputElement>();
  for (const group of radiosByName.values()) {
    const checked = group.find((radio) => radio.checked);
    tabbableRadio.add(checked ?? group[0]);
  }

  return elements.filter((el) => {
    if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || !el.name) return true;
    return tabbableRadio.has(el);
  });
}

export function findFocusableElements(root: ParentNode = document): HTMLElement[] {
  const elements = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)) as HTMLElement[];
  return filterRadioGroups(elements);
}

/**
 * Finds the next or previous focusable element in document tab order.
 * Does not wrap around the page — returns null if at the boundary.
 */
export function findNextFocusableElement(
  currentElement: HTMLElement,
  direction: 'next' | 'previous'
): HTMLElement | null {
  const allFocusableElements = findFocusableElements();

  const currentIndex = Array.from(allFocusableElements).indexOf(currentElement);
  if (currentIndex === -1) return null;

  const targetIndex = currentIndex + (direction === 'next' ? 1 : -1);
  if (targetIndex < 0 || targetIndex >= allFocusableElements.length) {
    return null;
  }

  return allFocusableElements[targetIndex] as HTMLElement;
}
